import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Form } from 'react-bootstrap';
import { sprayService, SpraySession, SessionFilters, Orchard, SprayMachine, SprayOperator, SprayProgram } from '../../services/sprayService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageHeader from '../shell/PageHeader';
import { DataTable, Field, FilterBar, FilterBarChip, SortState } from '../patterns';
import IdChip from './IdChip';
import { SessionStatusPill } from './statusPills';
import { getPrimaryOrchardId, getPrimaryOrchardName } from '../../utils/spraySessionDisplay';
import BulkActionToolbar, { BulkAction } from '../common/BulkActionToolbar';
import { formatTime } from '../../dateFormat';

/**
 * Resolve the round seq for a session by looking up the linked round in
 * the populated programs catalogue. Returns `undefined` for legacy
 * sessions (no roundId) or when the round has no seq assigned. Used by
 * the default sort on the Sessions list (track
 * spray_session_ui_changes_20260502 / Phase 4).
 */
function getRoundSeq(session: SpraySession, programs: SprayProgram[]): number | undefined {
  if (!session.roundId) return undefined;
  for (const p of programs) {
    const r = (p.rounds || []).find(rr => rr._id === session.roundId);
    if (r) return r.seq;
  }
  return undefined;
}

/**
 * Resolve the display name for a session's machine. The list endpoint
 * returns `machineId` as a raw ObjectId string, but the type union allows
 * a populated `SprayMachine` object too — handle both. Falls back to '—'
 * when the field is unset or the id can't be matched against the fetched
 * machines list. Track: spray_session_ui_changes_20260502 / Phase 2.
 */
function getMachineName(session: SpraySession, machines: SprayMachine[]): string {
  if (!session.machineId) return '—';
  if (typeof session.machineId !== 'string') {
    return session.machineId.name ?? '—';
  }
  const m = machines.find(mm => mm._id === session.machineId);
  return m?.name ?? '—';
}

/**
 * Render the Operator cell content for a session: the first operator's
 * name, plus a `+N` Badge when there are more. Handles both populated
 * `SprayOperator` objects and raw ObjectId strings in `operatorIds`.
 * Returns '—' for sessions with no operators. Track:
 * spray_session_ui_changes_20260502 / Phase 2.
 */
function renderOperatorCell(
  session: SpraySession,
  operators: SprayOperator[],
  t: (key: string) => string,
): React.ReactNode {
  const ids = session.operatorIds ?? [];
  if (ids.length === 0) return '—';
  const first = ids[0];
  const firstName = typeof first === 'string'
    ? operators.find(op => op._id === first)?.name
    : first.name;
  if (!firstName) return '—';
  const extra = ids.length - 1;
  return (
    <>
      {firstName}
      {extra > 0 && (
        <Badge bg="light" text="dark" pill className="ms-2" data-testid="operator-extra-badge">
          {`+${extra}`}
        </Badge>
      )}
    </>
  );
}

/**
 * Format an ISO date string as HH:MM (24-hour) time-of-day via the shared
 * dateFormat chokepoint. Returns null when the input is missing or not a
 * valid date — the cell renderer then falls through to '—'.
 */
function formatTimeOfDay(iso?: string | null): string | null {
  if (!iso) return null;
  const formatted = formatTime(iso);
  return formatted === 'N/A' ? null : formatted;
}

/**
 * Render the value for the session-list Start Time / End Time cells.
 *
 * Times live only in the parent's `startDate`/`endDate` Date fields, which the
 * recompute service propagates from per-combo `startedAt`/`endedAt`. The legacy
 * `beginTime`/`endTime` Strings were removed in #162.
 */
function renderTimeCell(dateValue?: string | null): string {
  return formatTimeOfDay(dateValue) ?? '—';
}

/**
 * Status bucket for row-click dispatch. Active-bucket rows (draft, planned)
 * route to the Edit Page; everything else (completed, cancelled, deleted,
 * in_progress) routes to the Overview Page. Track:
 * spray_session_ui_changes_20260502 / Phase 1 / Task 2.
 */
const ACTIVE_STATUSES: ReadonlyArray<string> = ['draft', 'planned'];

type StatusChipId = 'all' | 'planned' | 'inProgress' | 'completed' | 'cancelled' | 'deleted';

export interface BuildChipsArgs {
  counts: {
    all: number;
    planned: number;
    inProgress: number;
    completed: number;
    cancelled: number;
    deleted: number;
  };
  statusFilter: StatusChipId;
  programIdFilter?: string;
  programNameDisplay?: string;
  roundIdFilter?: string;
  roundNameDisplay?: string;
  showDeleted: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Pure function: builds the FilterBar chip array for the Sessions list from
 * current filter state. Extracted from the inline chip-build block so it can
 * be unit-tested independently of the React component tree.
 *
 * Track: spray_session_ui_changes_20260502 / Phase C2 / Task 22.
 */
export function buildChips(args: BuildChipsArgs): FilterBarChip[] {
  const { counts, statusFilter, programIdFilter, programNameDisplay, roundIdFilter, roundNameDisplay, showDeleted, t } = args;
  const chips: FilterBarChip[] = [
    { id: 'all',        label: t('spray.filters.all'),        count: counts.all,        active: statusFilter === 'all' },
    { id: 'planned',    label: t('spray.filters.planned'),    count: counts.planned,    active: statusFilter === 'planned' },
    { id: 'inProgress', label: t('spray.filters.inProgress'), count: counts.inProgress, active: statusFilter === 'inProgress' },
    { id: 'completed',  label: t('spray.filters.completed'),  count: counts.completed,  active: statusFilter === 'completed' },
    { id: 'cancelled',  label: t('spray.filters.cancelled'),  count: counts.cancelled,  active: statusFilter === 'cancelled' },
  ];
  if (showDeleted) {
    chips.push({
      id: 'deleted',
      label: t('spray.filters.deleted'),
      count: counts.deleted,
      active: statusFilter === 'deleted',
    });
  }
  if (programIdFilter && programNameDisplay) {
    chips.push({
      id: 'clear-program',
      label: `${t('spray.sessions.programChip', { name: programNameDisplay })} ✕`,
      active: true,
    });
  }
  if (roundIdFilter && roundNameDisplay) {
    chips.push({
      id: 'clear-round',
      label: `${t('spray.sessions.roundChip', { name: roundNameDisplay })} ✕`,
      active: true,
    });
  }
  return chips;
}

/**
 * Map the camelCase chip id back to the snake_case status string used by
 * the server / mock data. `all` means "no client-side narrowing".
 */
const STATUS_FOR_CHIP: Record<StatusChipId, string | null> = {
  all: null,
  planned: 'planned',
  inProgress: 'in_progress',
  completed: 'completed',
  cancelled: 'cancelled',
  deleted: 'deleted',
};

interface Props {
  onNewSession?: () => void;
  /**
   * Fired when an active-bucket row (draft / planned) is clicked. Receives
   * the full session so the parent can hand it to SpraySessionForm without
   * a second fetch. Inactive rows do NOT fire this callback.
   */
  onEditSession?: (session: SpraySession) => void;
  /**
   * Fired when a terminal-ish row (completed / cancelled / deleted /
   * in_progress) is clicked. Receives just the session id since the parent
   * loads the detail view from the server.
   */
  onViewSession?: (id: string) => void;
  /**
   * Optional hierarchical program/round filter passed in from
   * SpraySessionsPage (which owns the URL state). Their presence drives
   * the breadcrumb chip UX above the session table; their absence
   * restores the unfiltered view.
   */
  programIdFilter?: string;
  roundIdFilter?: string;
  /** ISO date string or 'today' shorthand; narrows sessions to a single day. */
  dayFilter?: string;
  programNameDisplay?: string;
  roundNameDisplay?: string;
  onClearProgramFilter?: () => void;
  onClearRoundFilter?: () => void;
}

const SpraySessionList: React.FC<Props> = ({
  onNewSession, onEditSession, onViewSession,
  programIdFilter, roundIdFilter, dayFilter, programNameDisplay, roundNameDisplay,
  onClearProgramFilter, onClearRoundFilter,
}) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<SpraySession[]>([]);
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [machines, setMachines] = useState<SprayMachine[]>([]);
  const [operators, setOperators] = useState<SprayOperator[]>([]);
  const [programs, setPrograms] = useState<SprayProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [orchardFilter, setOrchardFilter] = useState('');
  const [orchardSortAsc, setOrchardSortAsc] = useState<boolean | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusChipId>('all');

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.sessions.title') },
  ]);

  useEffect(() => {
    const filters: SessionFilters = {};
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    if (showDeleted) filters.status = 'all';
    if (programIdFilter) filters.programId = programIdFilter;
    // Hierarchical guard: only forward roundId if programId is also set.
    // The page strips the bare roundId from the URL on mount, so this is
    // belt-and-braces — but it keeps the API call clean if the props
    // somehow drift.
    if (roundIdFilter && programIdFilter) filters.roundId = roundIdFilter;
    if (dayFilter) filters.day = dayFilter;
    setLoading(true);
    setError('');
    sprayService.getSessions(filters)
      .then(setSessions)
      .catch((err: any) => setError(err.message ?? t('spray.sessions.loadError')))
      .finally(() => setLoading(false));
  // `t` deliberately omitted: react-i18next can return a fresh `t` on
  // unrelated re-renders, which would re-trigger the fetch and briefly
  // hide the table. Reading t() inside the catch is safe — JS closes
  // over the latest reference at call time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, showDeleted, fetchKey, programIdFilter, roundIdFilter, dayFilter]);

  // Row-level Delete and Restore handlers were removed by track
  // spray_session_ui_changes_20260502 / Phase 2 / Task 3. Deletion and
  // restoration both flow through the BulkActionToolbar above the table
  // (see bulkActions below); the toolbar surfaces the same status-aware
  // confirmation copy via its `confirm` callbacks.

  useEffect(() => {
    sprayService.getOrchards().then(setOrchards).catch(() => {});
    // Track spray_session_ui_changes_20260502 / Phase 2: the list resolves
    // machine and operator names client-side against the same fetched
    // catalogues the form uses. The /sessions list endpoint does NOT
    // populate these references (only /sessions/:id does), so the cross-
    // reference is necessary to render real names instead of ObjectIds.
    sprayService.getMachines().then(setMachines).catch(() => {});
    sprayService.getOperators().then(setOperators).catch(() => {});
    // Programs power the round.seq lookup used by the default list sort
    // (Phase 4). Each program embeds its rounds, so this single fetch
    // provides everything getRoundSeq() needs.
    sprayService.getPrograms().then(setPrograms).catch(() => {});
  }, []);

  const displayedSessions = useMemo(() => {
    let rows = sessions;

    // Status chip narrowing — client-side, over rows already returned.
    const statusForChip = STATUS_FOR_CHIP[statusFilter];
    if (statusForChip !== null) {
      rows = rows.filter(s => (s.status ?? '') === statusForChip);
    }

    // Free-text name search.
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(s => (s.name ?? '').toLowerCase().includes(q));
    }

    if (orchardFilter) {
      rows = rows.filter(s => getPrimaryOrchardId(s) === orchardFilter);
    }
    if (orchardSortAsc !== null) {
      const dir = orchardSortAsc ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const an = getPrimaryOrchardName(a, orchards) ?? '';
        const bn = getPrimaryOrchardName(b, orchards) ?? '';
        // Natural / numeric-aware sort so "Block 9" comes before "Block 10".
        return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' }) * dir;
      });
    } else {
      // Default sort (Phase 4 / track spray_session_ui_changes_20260502):
      // ascending by session.seq, then by round.seq. Legacy entries
      // (undefined seq on either) sink to the end via Infinity sentinel.
      rows = [...rows].sort((a, b) => {
        const aSeq = a.seq ?? Infinity;
        const bSeq = b.seq ?? Infinity;
        if (aSeq !== bSeq) return aSeq - bSeq;
        const aRound = getRoundSeq(a, programs) ?? Infinity;
        const bRound = getRoundSeq(b, programs) ?? Infinity;
        return aRound - bRound;
      });
    }
    return rows;
  }, [sessions, orchards, programs, orchardFilter, orchardSortAsc, search, statusFilter]);

  // Sort state for the DataTable. Only the orchard column is sortable; when
  // orchardSortAsc is null the default seq-based sort applies and we pass
  // null so no header carries aria-sort.
  const sort: SortState | null = orchardSortAsc === null
    ? null
    : { key: 'orchard', direction: orchardSortAsc ? 'asc' : 'desc' };

  const handleSortChange = (next: SortState) => {
    if (next.key !== 'orchard') return;
    setOrchardSortAsc(next.direction === 'asc');
  };

  // Bulk-selection helpers (Phase 4 spec §4): selection covers ALL visible
  // rows so users can bulk-restore deleted sessions when the toggle is ON.
  // Defined after displayedSessions so they capture the latest filtered set.
  const toggleSelected = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = displayedSessions.length > 0
    && displayedSessions.every(s => selectedIds.has(s._id));
  const toggleSelectAll = () => {
    // Selection toggles only over CURRENTLY-visible rows, preserving any
    // selections on rows hidden by an active client-side filter (chip /
    // search / orchard). Matches the Programs-list pattern from commit
    // 94e53ffa.
    if (allVisibleSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        displayedSessions.forEach(s => next.delete(s._id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        displayedSessions.forEach(s => next.add(s._id));
        return next;
      });
    }
  };

  const selectedSessions = sessions.filter(s => selectedIds.has(s._id));

  const bulkActions: BulkAction<SpraySession>[] = [
    {
      key: 'bulk-delete',
      label: t('spray.sessions.bulkDelete'),
      variant: 'danger',
      applicableWhen: s => s.status !== 'deleted',
      onClick: async items => {
        try {
          await sprayService.bulkDeleteSessions(items.map(s => s._id));
          setSelectedIds(new Set());
          setFetchKey(k => k + 1);
        } catch {
          setError(t('spray.sessions.deleteError'));
        }
      },
      confirm: {
        title: t('spray.sessions.confirmBulkDeleteTitle'),
        body: count => t('spray.sessions.confirmBulkDeleteBody', { count }),
        confirmLabel: t('spray.sessions.bulkDelete'),
        variant: 'danger',
      },
    },
    {
      key: 'bulk-restore',
      label: t('spray.sessions.bulkRestore'),
      variant: 'success',
      applicableWhen: s => s.status === 'deleted',
      onClick: async items => {
        try {
          await sprayService.bulkRestoreSessions(items.map(s => s._id));
          setSelectedIds(new Set());
          setFetchKey(k => k + 1);
        } catch {
          setError(t('spray.sessions.restoreError'));
        }
      },
      confirm: {
        title: t('spray.sessions.confirmBulkRestoreTitle'),
        body: count => t('spray.sessions.confirmBulkRestoreBody', { count }),
        confirmLabel: t('spray.sessions.bulkRestore'),
        variant: 'success',
      },
    },
  ];

  // Per-status counts, computed against the unfiltered fetched rows so the
  // numbers don't tug as the user types into the search box.
  const counts = useMemo(() => ({
    all:        sessions.length,
    planned:    sessions.filter(s => s.status === 'planned').length,
    inProgress: sessions.filter(s => s.status === 'in_progress').length,
    completed:  sessions.filter(s => s.status === 'completed').length,
    cancelled:  sessions.filter(s => s.status === 'cancelled').length,
    deleted:    sessions.filter(s => s.status === 'deleted').length,
  }), [sessions]);

  // Build the chip array via the exported pure helper — status chips first,
  // then the hierarchical program/round filter chips. See buildChips() above.
  const chips = buildChips({ counts, statusFilter, programIdFilter, programNameDisplay, roundIdFilter, roundNameDisplay, showDeleted, t });

  const handleChipClick = (id: string) => {
    if (id === 'clear-program') {
      // The parent's clearProgramFilter already cascades to clear roundId
      // too (hierarchical contract).
      onClearProgramFilter?.();
      return;
    }
    if (id === 'clear-round') {
      onClearRoundFilter?.();
      return;
    }
    setStatusFilter(id as StatusChipId);
  };

  const handleRowClick = (session: SpraySession) => {
    // Active-bucket rows route to Edit; the rest route to Overview.
    // Each branch no-ops silently when the matching callback is not
    // wired, so legacy callers passing only one of the two props
    // continue to render without runtime errors.
    const isActiveBucket = ACTIVE_STATUSES.includes(session.status ?? '');
    if (isActiveBucket) {
      onEditSession?.(session);
    } else {
      onViewSession?.(session._id);
    }
  };

  return (
    <div data-testid="spray-session-list">
      <PageHeader
        title={t('spray.sessions.title')}
        meta={t('spray.sessions.metaCount', { count: displayedSessions.length })}
        actions={
          <div className="d-flex align-items-center gap-3">
            <Form.Check
              type="switch"
              id="show-deleted-sessions-toggle"
              label={t('spray.sessions.showDeleted')}
              checked={showDeleted}
              onChange={() => {
                setShowDeleted(prev => {
                  // When turning showDeleted OFF, reset a 'deleted' chip filter
                  // so the user isn't left with an empty/inconsistent view.
                  if (prev) setStatusFilter(sf => sf === 'deleted' ? 'all' : sf);
                  return !prev;
                });
              }}
            />
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={() => onNewSession?.()}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" /> {t('spray.pageHeader.actions.newSession')}
            </button>
          </div>
        }
      />

      {error && <Alert variant="danger" role="alert" className="mb-3">{error}</Alert>}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('spray.sessions.searchPlaceholder')}
        chips={chips}
        onChipClick={handleChipClick}
      />

      {/* Date / orchard filter controls — placed BELOW the FilterBar so the
          search + chip row is the first thing the user reaches, and the more
          situational filters live one row down. Orchard sort lives on the
          DataTable's Orchard column header (sortable), not as a standalone
          button. */}
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <Field label={t('spray.sessions.filterFrom')} controlId="session-filter-from">
          <input
            id="session-filter-from"
            type="date"
            className="pat-input"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
        </Field>
        <Field label={t('spray.sessions.filterTo')} controlId="session-filter-to">
          <input
            id="session-filter-to"
            type="date"
            className="pat-input"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
        </Field>
        <Field label={t('spray.sessions.filterByOrchard')} controlId="session-filter-orchard">
          <select
            id="session-filter-orchard"
            className="pat-input"
            value={orchardFilter}
            onChange={e => setOrchardFilter(e.target.value)}
          >
            <option value="">{t('spray.sessions.filterAll')}</option>
            {orchards.map(o => (
              <option key={o._id} value={o._id}>{o.orchardName}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* Select-all + bulk-action toolbar row — sits between the filter row
          and the DataTable, mirroring the Programs-list option B from
          Task 11 / commit 69f5c3d2. */}
      {!loading && sessions.length > 0 && (
        <div className="d-flex align-items-center gap-3 mb-2 px-2">
          <input
            type="checkbox"
            className="pat-checkbox"
            checked={allVisibleSelected}
            onChange={toggleSelectAll}
            aria-label={t('spray.sessions.selectAllSessions')}
          />
          <BulkActionToolbar
            selectedItems={selectedSessions}
            actions={bulkActions}
            onClearSelection={() => setSelectedIds(new Set())}
            clearLabel={t('spray.sessions.clearSelection')}
          />
        </div>
      )}

      {loading && <div>{t('common.loading')}</div>}

      {!loading && !error && sessions.length === 0 && (
        <p className="text-muted">{t('spray.sessions.noSessions')}</p>
      )}

      {!loading && !error && sessions.length > 0 && (
        <DataTable<SpraySession>
          rows={displayedSessions}
          rowKey={(s) => s._id}
          rowClassName={(s) => (s.status === 'deleted' ? 'text-muted' : '')}
          onRowClick={handleRowClick}
          sort={sort}
          onSortChange={handleSortChange}
          emptyState={
            (() => {
              const isFiltered =
                search.trim().length > 0 ||
                statusFilter !== 'all' ||
                Boolean(programIdFilter) ||
                Boolean(roundIdFilter);
              return isFiltered
                ? <p className="text-muted">{t('spray.sessions.noMatches')}</p>
                : undefined;
            })()
          }
          columns={[
            {
              key: 'select',
              label: '',
              render: (s) => (
                <input
                  type="checkbox"
                  className="pat-checkbox"
                  checked={selectedIds.has(s._id)}
                  onChange={(e) => { e.stopPropagation(); toggleSelected(s._id); }}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select ${s.name ?? s._id}`}
                />
              ),
            },
            {
              key: 'sessionName',
              label: t('spray.sessions.sessionName'),
              render: (s) => (
                <span>
                  {s.name ?? '—'}
                  <IdChip prefix="SES" seq={s.seq} />
                  {/* Legacy session predicate (spec §3 + §8.2): no roundId
                      → predates the round-link contract → render the
                      'Legacy' badge so the operator can recognise the older
                      shape at a glance. Kept as react-bootstrap Badge for
                      now — there is no `pat-badge--warning` and adding one
                      is out of scope for Task 13. */}
                  {s.roundId == null && (
                    <Badge
                      bg="warning"
                      text="dark"
                      pill
                      className="ms-2"
                      data-testid="legacy-badge"
                    >
                      {t('spray.sessions.legacyBadge')}
                    </Badge>
                  )}
                </span>
              ),
            },
            {
              key: 'orchard',
              label: t('spray.sessions.orchard'),
              sortable: true,
              render: (s) => getPrimaryOrchardName(s, orchards) ?? '—',
            },
            {
              key: 'date',
              label: t('spray.sessions.date'),
              render: (s) => s.date.substring(0, 10),
            },
            {
              key: 'status',
              label: t('spray.sessions.status'),
              render: (s) => (s.status ? <SessionStatusPill status={s.status} /> : '—'),
            },
            {
              key: 'beginTime',
              label: t('spray.sessions.beginTime'),
              // Phase 6b: the recompute-service-derived startDate (min
              // combo.startedAt) is the only source now — the legacy
              // `beginTime` string was removed in #162.
              render: (s) => renderTimeCell(s.startDate),
            },
            {
              key: 'endTime',
              label: t('spray.sessions.endTime'),
              // Same Phase 6b source as beginTime — endDate is only
              // populated once all combos are terminal, so in-progress
              // sessions correctly show '—' until then.
              render: (s) => renderTimeCell(s.endDate),
            },
            {
              key: 'machine',
              label: t('spray.sessions.machine'),
              render: (s) => getMachineName(s, machines),
            },
            {
              key: 'operator',
              label: t('spray.sessions.operator'),
              render: (s) => renderOperatorCell(s, operators, t),
            },
          ]}
        />
      )}
    </div>
  );
};

export default SpraySessionList;
